/**
 * Auth functions are used for reaching the endpoints for sending / accepting invitations and resetting passwords. Also for login, logout, and refreshing the JWT auth token.
 * @module Auth
 * @since 0.0.4
 */
import GlobalFunctions from '../global'

module.exports = {
	/**
	 * Call to unoapp API that accepts an email address and generates a token. Also sends an email to the email address with a link to the front-end page for accepting and resetting the password. Note that we do NOT output an error if there is no user registered under the specified email address. If no user was registered under the email address, no email will be sent to the user's email address to prevent possible spam.
	 * @function
	 * @param {string} email - The users email.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.5
	 */
	forgotPassword (email) {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/auth/reset_password',
				data: {email: email},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that accepts request token and returns the invitee's email address or informs you that the token has expired or is invalid.
	 * @function
	 * @param {string} invitationToken - The invitation token to be tested.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.5
	 */
	checkInvitationTokenExpiry (invitationToken) {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/auth/validate_request',
				data: {token: invitationToken},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that accepts token and password, verifies that password passes some basic requirements.
	 * @function
	 * @param {string} invitationToken - The invitation token to specify that it is not an
	 *									 expired request and the user who has requested it.
	 * @param {string} password - The password to be set.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.5
	 */
	setPassword (invitationToken, password) {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/auth/set_password',
				data: {token: invitationToken, password: password},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that accepts an email address with optional first and last names and generates a token and returns the new user ID. Also sends an email to the email address with a link to the front-end page for accepting the invitation and registering a new password. A user cannot be invited to the same business or location more than once. Note that a user cannot have more than 10 accounts, so sending an invitation to a user with 10 accounts will fail.
	 * @function
	 * @param {object} user - The details of the user to be invited.
	 * @param {integer} businessId - The business id of the business that the user is being invited to.
	 * @param {integer} locationId - The location id of the location that the user is invited to.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.8
	 */
	inviteUser (user, businessId, locationId) {
		let httpParam = {
			email: user.email,
			business_id: businessId
		}
		if (locationId) {
			httpParam.location_id = locationId
			httpParam.account_type = 'location'
		} else {
			httpParam.account_type = 'business'
		}
		if (!user.name.first_name || !user.name.first_name.length) {
			delete user.name.first_name
		}
		if (!user.name.last_name || !user.name.last_name.length) {
			delete user.name.last_name
		}
		if (locationId) {
			httpParam.location_role_id = user.location_role.role_id
		} else {
			if (user.role) {
				httpParam.role_id = user.role.role_id
			} else {
				httpParam.role_id = user.role_id
			}
		}

		httpParam.name = user.name
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/auth/send_invitation',
				data: httpParam,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API to resend an invitation email that has expired
	 * @function
	 * @param {integer} accountId - The id of the account to be emailed
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.8
	 */
	resendInvite (accountId) {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/auth/resend_invitation',
				data: {account_id: accountId},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that accepts an email address and password and validates the user credentials. If credentials are valid, a JWT token is generated for authentication purposes. Returns a unix timestamp of when the JWT token should be considered expired.
	 * @function
	 * @param {string} email - The users email.
	 * @param {string} password - The users password.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.5
	 */
	login (email, password) {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/auth/login',
				data: {email: email, password: password},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that invalidates current active JWT token and destroys JWT token cookie. Enabling the user_idle flag will bypass the login logs.
	 * @function
	 * @param {boolean} userIdle - Whether this happened because the user was idle or clicked the button.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.5
	 */
	logOut (userIdle) {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/auth/logout',
				data: {user_idle: userIdle},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that invalidates current active JWT token and generates a new JWT token, overwriting the existing JWT token cookie. Returns a unix timestamp of when the JWT token should be considered expired.
	 * @function
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.5
	 */
	refresh () {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/auth/refresh',
				data: {},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that selects an account that belongs to the user.
	 * @function
	 * @param {integer} accountId - The account_id of the account that the user is logging in to.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.5
	 */
	loginToAccount (accountId) {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/auth/account/' + accountId,
				data: {},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	}
}



// WEBPACK FOOTER //
// ./src/controllers/Auth.js